import { useMemo } from 'react';
import {
  MIN_TRANSACTIONS_FOR_INSIGHTS,
  MIN_MONTHS_FOR_STABILITY,
  EXPENSE_RATIO_THRESHOLDS,
  SAVINGS_RATE_THRESHOLDS,
  INCOME_STABILITY_THRESHOLDS,
  FIXED_CATEGORIES,
  TOP_CATEGORIES_COUNT,
} from '../components/analytics/insightsConstants';

/**
 * 금액을 천 단위 콤마가 포함된 문자열로 포맷팅한다.
 * @param {number} amount - 포맷팅할 금액 (양의 정수)
 * @returns {string} 천 단위 콤마가 포함된 문자열 (예: 1000 → "1,000")
 */
export const formatAmount = (amount) => {
  return Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * 거래 금액이 유효한지 검증한다.
 * 유효: 숫자이며 양수 (NaN, 음수 제외)
 * @param {*} amount - 검증할 금액 값
 * @returns {boolean}
 */
export const isValidAmount = (amount) => {
  const num = Number(amount);
  return !isNaN(num) && isFinite(num) && num > 0;
};

/**
 * 기간별 거래 필터링 함수
 * @param {Array} transactions - 전체 거래 배열
 * @param {string} timeRange - 기간 ('7days'|'30days'|'3months'|'6months'|'12months'|'custom')
 * @param {Object} customDateRange - { start: string, end: string } (custom일 때만 사용)
 * @returns {Array} 필터링된 거래 배열
 */
export const filterTransactionsByPeriod = (transactions, timeRange, customDateRange) => {
  if (!transactions || !Array.isArray(transactions)) {
    return [];
  }

  const now = new Date();
  let startDate;
  let endDate = now;

  switch (timeRange) {
    case '7days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '30days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 29);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '3months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '6months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '12months':
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      if (customDateRange && customDateRange.start && customDateRange.end) {
        startDate = new Date(customDateRange.start);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customDateRange.end);
        endDate.setHours(23, 59, 59, 999);
      } else {
        return [];
      }
      break;
    default:
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      startDate.setHours(0, 0, 0, 0);
      break;
  }

  return transactions.filter((transaction) => {
    if (!transaction || !transaction.date) {
      return false;
    }
    const transactionDate = new Date(transaction.date);
    if (isNaN(transactionDate.getTime())) {
      return false;
    }
    return transactionDate >= startDate && transactionDate <= endDate;
  });
};

/**
 * 지출비율을 분류한다.
 * @param {number} ratio - 지출비율 (0~100+)
 * @returns {{ value: number, status: string, colorClass: string }}
 */
export const classifyExpenseRatio = (ratio) => {
  if (ratio <= EXPENSE_RATIO_THRESHOLDS.good.max) {
    return {
      value: ratio,
      status: EXPENSE_RATIO_THRESHOLDS.good.label,
      colorClass: EXPENSE_RATIO_THRESHOLDS.good.colorClass,
    };
  }
  if (ratio <= EXPENSE_RATIO_THRESHOLDS.warning.max) {
    return {
      value: ratio,
      status: EXPENSE_RATIO_THRESHOLDS.warning.label,
      colorClass: EXPENSE_RATIO_THRESHOLDS.warning.colorClass,
    };
  }
  return {
    value: ratio,
    status: EXPENSE_RATIO_THRESHOLDS.danger.label,
    colorClass: EXPENSE_RATIO_THRESHOLDS.danger.colorClass,
  };
};

/**
 * 저축률을 분류한다.
 * @param {number} rate - 저축률
 * @returns {{ value: number, status: string, colorClass: string }}
 */
export const classifySavingsRate = (rate) => {
  if (rate >= SAVINGS_RATE_THRESHOLDS.excellent.min) {
    return {
      value: rate,
      status: SAVINGS_RATE_THRESHOLDS.excellent.label,
      colorClass: SAVINGS_RATE_THRESHOLDS.excellent.colorClass,
    };
  }
  if (rate >= SAVINGS_RATE_THRESHOLDS.normal.min) {
    return {
      value: rate,
      status: SAVINGS_RATE_THRESHOLDS.normal.label,
      colorClass: SAVINGS_RATE_THRESHOLDS.normal.colorClass,
    };
  }
  return {
    value: rate,
    status: SAVINGS_RATE_THRESHOLDS.needsImprovement.label,
    colorClass: SAVINGS_RATE_THRESHOLDS.needsImprovement.colorClass,
  };
};

/**
 * 월별 수입 배열로부터 수입안정성을 계산한다.
 * 공식: 100 - (표준편차 / 평균) × 100, [0, 100] 범위로 클램핑
 * 2개월 미만 데이터 시 '데이터부족' 반환
 * @param {number[]} monthlyIncomes - 월별 수입 합계 배열
 * @returns {{ value: number|string, status: string, colorClass: string }}
 */
export const calculateIncomeStability = (monthlyIncomes) => {
  if (!monthlyIncomes || monthlyIncomes.length < MIN_MONTHS_FOR_STABILITY) {
    return {
      value: '-',
      status: '데이터부족',
      colorClass: 'insights-status--warning',
    };
  }

  const mean = monthlyIncomes.reduce((sum, v) => sum + v, 0) / monthlyIncomes.length;

  if (mean === 0) {
    return {
      value: '-',
      status: '데이터부족',
      colorClass: 'insights-status--warning',
    };
  }

  const variance =
    monthlyIncomes.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / monthlyIncomes.length;
  const stdDev = Math.sqrt(variance);

  const stability = Math.max(0, Math.min(100, 100 - (stdDev / mean) * 100));
  const rounded = Math.round(stability * 10) / 10;

  if (rounded >= INCOME_STABILITY_THRESHOLDS.stable.min) {
    return {
      value: rounded,
      status: INCOME_STABILITY_THRESHOLDS.stable.label,
      colorClass: INCOME_STABILITY_THRESHOLDS.stable.colorClass,
    };
  }
  if (rounded >= INCOME_STABILITY_THRESHOLDS.normal.min) {
    return {
      value: rounded,
      status: INCOME_STABILITY_THRESHOLDS.normal.label,
      colorClass: INCOME_STABILITY_THRESHOLDS.normal.colorClass,
    };
  }
  return {
    value: rounded,
    status: INCOME_STABILITY_THRESHOLDS.unstable.label,
    colorClass: INCOME_STABILITY_THRESHOLDS.unstable.colorClass,
  };
};

/**
 * 거래 배열에서 고유한 월 수를 계산한다.
 * @param {Array} transactions - 유효한 거래 배열
 * @returns {number} 고유 월 수 (최소 1)
 */
export const calculateMonthCount = (transactions) => {
  if (!transactions || transactions.length === 0) {
    return 1;
  }

  const months = new Set();
  for (const t of transactions) {
    if (t.date) {
      const monthKey = t.date.slice(0, 7); // 'YYYY-MM'
      if (monthKey.length === 7) {
        months.add(monthKey);
      }
    }
  }

  return Math.max(1, months.size);
};

/**
 * 지출 거래에서 가장 많이 지출한 카테고리를 반환한다.
 * @param {Array} expenseTransactions - 유효한 지출 거래 배열
 * @returns {{ name: string, amount: number, percentage: number } | null}
 */
export const getTopCategory = (expenseTransactions) => {
  if (!expenseTransactions || expenseTransactions.length === 0) {
    return null;
  }

  const categoryMap = {};
  let totalExpense = 0;

  for (const t of expenseTransactions) {
    const amount = Number(t.amount);
    const category = t.category && t.category.trim() !== '' ? t.category : '미분류';
    categoryMap[category] = (categoryMap[category] || 0) + amount;
    totalExpense += amount;
  }

  if (totalExpense === 0) {
    return null;
  }

  let topName = null;
  let topAmount = 0;

  for (const [category, amount] of Object.entries(categoryMap)) {
    if (amount > topAmount) {
      topName = category;
      topAmount = amount;
    }
  }

  return {
    name: topName,
    amount: topAmount,
    percentage: Math.round((topAmount / totalExpense) * 100 * 10) / 10,
  };
};

/**
 * 저축률에 따른 재정조언 메시지를 반환한다.
 * @param {number} savingsRate - 저축률 (%)
 * @returns {{ message: string, type: string }}
 */
export const getFinancialAdvice = (savingsRate) => {
  if (savingsRate >= 20) {
    return { message: '훌륭합니다! 현재 저축 습관을 유지하세요.', type: 'positive' };
  }
  if (savingsRate >= 10) {
    return { message: '좋은 시작입니다. 조금 더 절약하면 목표에 가까워집니다.', type: 'improvement' };
  }
  if (savingsRate >= 0) {
    return { message: '지출을 줄이고 저축 비율을 높여보세요.', type: 'saving' };
  }
  return { message: '지출이 수입을 초과합니다. 지출 관리가 필요합니다.', type: 'warning' };
};

/**
 * 카테고리별 지출 합산 및 비율 계산
 * 상위 TOP_CATEGORIES_COUNT개 카테고리를 금액 내림차순으로 반환한다.
 * @param {Array} expenseTransactions - 유효한 지출 거래 배열
 * @returns {Array<{ category: string, amount: number, percentage: number }>}
 */
export const calculateCategoryTotals = (expenseTransactions) => {
  if (!expenseTransactions || expenseTransactions.length === 0) {
    return [];
  }

  const categoryMap = {};
  let totalExpense = 0;

  for (const t of expenseTransactions) {
    const amount = Number(t.amount);
    const category = t.category && t.category.trim() !== '' ? t.category : '미분류';
    categoryMap[category] = (categoryMap[category] || 0) + amount;
    totalExpense += amount;
  }

  if (totalExpense === 0) {
    return [];
  }

  const sorted = Object.entries(categoryMap)
    .map(([category, amount]) => ({
      category,
      amount,
      percentage: (amount / totalExpense) * 100,
    }))
    .sort((a, b) => b.amount - a.amount);

  return sorted.slice(0, TOP_CATEGORIES_COUNT);
};

/**
 * 정기지출(FIXED_CATEGORIES) / 변동지출 분할 계산
 * @param {Array} expenseTransactions - 유효한 지출 거래 배열
 * @returns {{ fixedExpense: { amount: number, percentage: number }, variableExpense: { amount: number, percentage: number }, totalExpense: number }}
 */
export const calculateExpenseSplit = (expenseTransactions) => {
  if (!expenseTransactions || expenseTransactions.length === 0) {
    return {
      fixedExpense: { amount: 0, percentage: 0 },
      variableExpense: { amount: 0, percentage: 0 },
      totalExpense: 0,
    };
  }

  let totalExpense = 0;
  let fixedAmount = 0;

  for (const t of expenseTransactions) {
    const amount = Number(t.amount);
    const category = t.category && t.category.trim() !== '' ? t.category : '미분류';
    totalExpense += amount;
    if (FIXED_CATEGORIES.includes(category)) {
      fixedAmount += amount;
    }
  }

  if (totalExpense === 0) {
    return {
      fixedExpense: { amount: 0, percentage: 0 },
      variableExpense: { amount: 0, percentage: 0 },
      totalExpense: 0,
    };
  }

  const variableAmount = totalExpense - fixedAmount;

  return {
    fixedExpense: {
      amount: fixedAmount,
      percentage: (fixedAmount / totalExpense) * 100,
    },
    variableExpense: {
      amount: variableAmount,
      percentage: (variableAmount / totalExpense) * 100,
    },
    totalExpense,
  };
};

/**
 * 인사이트 데이터 계산 커스텀 훅
 * @param {Array} transactions - TransactionContext의 거래 배열
 * @param {string} timeRange - 선택된 기간 ('7days'|'30days'|'3months'|'6months'|'12months'|'custom')
 * @param {Object} customDateRange - { start: string, end: string } (custom일 때만)
 * @returns {Object} { balanceMetrics, summaryData, patternData, hasEnoughData }
 */
const useInsightsData = (transactions, timeRange, customDateRange) => {
  const safeTransactions = transactions || [];

  const filteredTransactions = useMemo(
    () => filterTransactionsByPeriod(safeTransactions, timeRange, customDateRange),
    [safeTransactions, timeRange, customDateRange]
  );

  const validTransactions = useMemo(
    () => filteredTransactions.filter((t) => isValidAmount(t.amount)),
    [filteredTransactions]
  );

  const hasEnoughData = validTransactions.length >= MIN_TRANSACTIONS_FOR_INSIGHTS;

  const balanceMetrics = useMemo(() => {
    const incomeTransactions = validTransactions.filter((t) => t.type === 'income');
    const expenseTransactions = validTransactions.filter((t) => t.type === 'expense');

    const totalIncome = incomeTransactions.reduce((sum, t) => sum + Number(t.amount), 0);
    const totalExpense = expenseTransactions.reduce((sum, t) => sum + Number(t.amount), 0);

    // Expense Ratio: (지출 / 수입) × 100, 수입 0일 때 0%
    const expenseRatioValue =
      totalIncome === 0 ? 0 : Math.round((totalExpense / totalIncome) * 100 * 10) / 10;
    const expenseRatio = classifyExpenseRatio(expenseRatioValue);

    // Savings Rate: ((수입 - 지출) / 수입) × 100, 수입 0일 때 0%
    const savingsRateValue =
      totalIncome === 0
        ? 0
        : Math.round(((totalIncome - totalExpense) / totalIncome) * 100 * 10) / 10;
    const savingsRate = classifySavingsRate(savingsRateValue);

    // Income Stability: 월별 수입 그룹핑 후 변동계수 기반 계산
    const monthlyIncomeMap = {};
    incomeTransactions.forEach((t) => {
      const monthKey = t.date ? t.date.slice(0, 7) : null; // 'YYYY-MM'
      if (monthKey) {
        monthlyIncomeMap[monthKey] = (monthlyIncomeMap[monthKey] || 0) + Number(t.amount);
      }
    });
    const monthlyIncomes = Object.values(monthlyIncomeMap);
    const incomeStability = calculateIncomeStability(monthlyIncomes);

    return { expenseRatio, savingsRate, incomeStability };
  }, [validTransactions]);

  const summaryData = useMemo(() => {
    const incomeTransactions = validTransactions.filter((t) => t.type === 'income');
    const expenseTransactions = validTransactions.filter((t) => t.type === 'expense');

    const totalIncome = incomeTransactions.reduce((sum, t) => sum + Number(t.amount), 0);
    const totalExpense = expenseTransactions.reduce((sum, t) => sum + Number(t.amount), 0);
    const balance = totalIncome - totalExpense;

    // 저축률: 수입 > 0 ? ((수입 - 지출) / 수입) * 100 : 0
    const savingsRate =
      totalIncome > 0
        ? Math.round(((totalIncome - totalExpense) / totalIncome) * 100 * 10) / 10
        : 0;

    // 월 수 계산 (최소 1)
    const monthCount = calculateMonthCount(validTransactions);

    // 월평균 수입/지출
    const avgMonthlyIncome = Math.round(totalIncome / monthCount);
    const avgMonthlyExpense = Math.round(totalExpense / monthCount);

    // 최다 지출 카테고리
    const topCategory = getTopCategory(expenseTransactions);

    // 재정조언
    const advice = getFinancialAdvice(savingsRate);

    return {
      totalIncome,
      totalExpense,
      balance,
      savingsRate,
      avgMonthlyIncome,
      avgMonthlyExpense,
      topCategory,
      advice,
    };
  }, [validTransactions]);

  const patternData = useMemo(() => {
    const expenseTransactions = validTransactions.filter((t) => t.type === 'expense');

    if (expenseTransactions.length === 0) {
      return {
        categoryTotals: [],
        fixedExpense: { amount: 0, percentage: 0 },
        variableExpense: { amount: 0, percentage: 0 },
        totalExpense: 0,
        isEmpty: true,
      };
    }

    const categoryTotals = calculateCategoryTotals(expenseTransactions);
    const { fixedExpense, variableExpense, totalExpense } =
      calculateExpenseSplit(expenseTransactions);

    return {
      categoryTotals,
      fixedExpense,
      variableExpense,
      totalExpense,
      isEmpty: false,
    };
  }, [validTransactions]);

  return {
    balanceMetrics,
    summaryData,
    patternData,
    hasEnoughData,
  };
};

export default useInsightsData;
